/**
 * Page /admin/compte — gestion du compte connecté.
 *
 * Permet à l'admin (et à n'importe quel user en pratique) de changer son
 * email et son mot de passe sans toucher au .env. Tout passe par les routes
 * PUT /api/auth/account/email et /api/auth/account/password.
 */
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    User as UserIcon,
    Mail,
    Lock,
    Loader2,
    LogOut,
    ShieldCheck,
    KeyRound,
    Smartphone,
    Copy,
    Check,
} from "lucide-react";

function _extractError(e) {
    const detail = e?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).join(" · ");
    if (detail && typeof detail === "object") return detail.msg || JSON.stringify(detail);
    return e?.message || "Erreur inconnue";
}

export default function AdminAccount() {
    const { user, logout, refresh } = useAuth();

    // Email
    const [emailCurrentPw, setEmailCurrentPw] = useState("");
    const [newEmail, setNewEmail] = useState("");
    const [emailSaving, setEmailSaving] = useState(false);

    // Password
    const [pwCurrent, setPwCurrent] = useState("");
    const [pwNew, setPwNew] = useState("");
    const [pwConfirm, setPwConfirm] = useState("");
    const [pwSaving, setPwSaving] = useState(false);

    // 2FA
    const [tfaSetup, setTfaSetup] = useState(null); // { secret, otpauth_uri }
    const [tfaCode, setTfaCode] = useState("");
    const [tfaBusy, setTfaBusy] = useState(false);
    const [tfaCopied, setTfaCopied] = useState(false);
    const [disablePw, setDisablePw] = useState("");
    const [disableCode, setDisableCode] = useState("");
    const [disableOpen, setDisableOpen] = useState(false);

    async function startTfaSetup() {
        setTfaBusy(true);
        try {
            const { data } = await api.post("/auth/2fa/setup");
            setTfaSetup(data);
            setTfaCode("");
        } catch (err) {
            toast.error("Échec : " + _extractError(err));
        } finally {
            setTfaBusy(false);
        }
    }

    async function activateTfa(e) {
        e.preventDefault();
        if (tfaCode.length < 6) { toast.error("Entre le code à 6 chiffres"); return; }
        setTfaBusy(true);
        try {
            await api.post("/auth/2fa/activate", { code: tfaCode });
            await refresh();
            setTfaSetup(null);
            setTfaCode("");
            toast.success("Double authentification activée");
        } catch (err) {
            toast.error("Échec : " + _extractError(err));
        } finally {
            setTfaBusy(false);
        }
    }

    async function disableTfa(e) {
        e.preventDefault();
        setTfaBusy(true);
        try {
            await api.post("/auth/2fa/disable", { current_password: disablePw, code: disableCode });
            await refresh();
            setDisableOpen(false);
            setDisablePw("");
            setDisableCode("");
            toast.success("Double authentification désactivée");
        } catch (err) {
            toast.error("Échec : " + _extractError(err));
        } finally {
            setTfaBusy(false);
        }
    }

    function copySecret() {
        if (!tfaSetup?.secret) return;
        navigator.clipboard?.writeText(tfaSetup.secret).then(() => {
            setTfaCopied(true);
            setTimeout(() => setTfaCopied(false), 2000);
        });
    }

    async function changeEmail(e) {
        e.preventDefault();
        if (!emailCurrentPw || !newEmail) {
            toast.error("Renseigne ton mot de passe et le nouvel email");
            return;
        }
        setEmailSaving(true);
        try {
            const { data } = await api.put("/auth/account/email", {
                current_password: emailCurrentPw,
                new_email: newEmail,
            });
            // Stocke le nouveau token (l'email est dans le payload JWT)
            localStorage.setItem("em_token", data.token);
            await refresh();
            toast.success("Email mis à jour");
            setEmailCurrentPw("");
            setNewEmail("");
        } catch (err) {
            toast.error("Échec : " + _extractError(err));
        } finally {
            setEmailSaving(false);
        }
    }

    async function changePassword(e) {
        e.preventDefault();
        if (!pwCurrent || !pwNew || !pwConfirm) {
            toast.error("Tous les champs sont requis");
            return;
        }
        if (pwNew !== pwConfirm) {
            toast.error("Les deux nouveaux mots de passe ne correspondent pas");
            return;
        }
        if (pwNew.length < 8) {
            toast.error("Au moins 8 caractères pour le nouveau mot de passe");
            return;
        }
        setPwSaving(true);
        try {
            await api.put("/auth/account/password", {
                current_password: pwCurrent,
                new_password: pwNew,
            });
            toast.success("Mot de passe mis à jour");
            setPwCurrent("");
            setPwNew("");
            setPwConfirm("");
        } catch (err) {
            toast.error("Échec : " + _extractError(err));
        } finally {
            setPwSaving(false);
        }
    }

    if (!user) return null;

    return (
        <div className="em-container py-8 max-w-3xl">
            <p className="em-eyebrow">Compte</p>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-primary mt-2">
                Mon compte
            </h1>
            <p className="text-muted-foreground mt-3">
                Sécurité et accès — modifier l'email et le mot de passe utilisés pour te connecter.
            </p>

            {/* Identité courante */}
            <div className="rounded-3xl border border-border bg-card p-5 mt-8 flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <UserIcon className="h-6 w-6" />
                </div>
                <div className="min-w-0">
                    <p className="font-display text-lg font-bold text-foreground truncate">
                        {user.name || "—"}
                    </p>
                    <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                </div>
                <span className="ml-auto inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold bg-primary text-primary-foreground">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    {user.role === "admin" ? "Administrateur" : "Utilisateur"}
                </span>
            </div>

            {/* Section : changer l'email */}
            <section className="rounded-3xl border border-border bg-card p-6 mt-6">
                <div className="flex items-center gap-2 mb-4">
                    <Mail className="h-5 w-5 text-primary" />
                    <h2 className="font-display text-xl font-bold text-foreground">Changer l'email</h2>
                </div>
                <form onSubmit={changeEmail} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="block">
                        <span className="text-xs font-semibold text-muted-foreground">Mot de passe actuel</span>
                        <input
                            type="password"
                            value={emailCurrentPw}
                            onChange={(e) => setEmailCurrentPw(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                            autoComplete="current-password"
                        />
                    </label>
                    <label className="block">
                        <span className="text-xs font-semibold text-muted-foreground">Nouvel email</span>
                        <input
                            type="email"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            placeholder={user.email}
                            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                        />
                    </label>
                    <div className="md:col-span-2 flex justify-end">
                        <button
                            type="submit"
                            disabled={emailSaving}
                            className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
                        >
                            {emailSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                            Mettre à jour l'email
                        </button>
                    </div>
                </form>
            </section>

            {/* Section : changer le mot de passe */}
            <section className="rounded-3xl border border-border bg-card p-6 mt-6">
                <div className="flex items-center gap-2 mb-4">
                    <KeyRound className="h-5 w-5 text-primary" />
                    <h2 className="font-display text-xl font-bold text-foreground">Changer le mot de passe</h2>
                </div>
                <p className="text-xs text-muted-foreground mb-4">
                    8 caractères minimum. Idéalement avec majuscules, chiffres et symboles.
                </p>
                <form onSubmit={changePassword} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="block md:col-span-2">
                        <span className="text-xs font-semibold text-muted-foreground">Mot de passe actuel</span>
                        <input
                            type="password"
                            value={pwCurrent}
                            onChange={(e) => setPwCurrent(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                            autoComplete="current-password"
                        />
                    </label>
                    <label className="block">
                        <span className="text-xs font-semibold text-muted-foreground">Nouveau mot de passe</span>
                        <input
                            type="password"
                            value={pwNew}
                            onChange={(e) => setPwNew(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                            autoComplete="new-password"
                        />
                    </label>
                    <label className="block">
                        <span className="text-xs font-semibold text-muted-foreground">Confirmation</span>
                        <input
                            type="password"
                            value={pwConfirm}
                            onChange={(e) => setPwConfirm(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                            autoComplete="new-password"
                        />
                    </label>
                    <div className="md:col-span-2 flex justify-end">
                        <button
                            type="submit"
                            disabled={pwSaving}
                            className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
                        >
                            {pwSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                            Mettre à jour le mot de passe
                        </button>
                    </div>
                </form>
            </section>

            {/* Section : double authentification (2FA) */}
            <section className="rounded-3xl border border-border bg-card p-6 mt-6">
                <div className="flex items-center gap-2 mb-2">
                    <Smartphone className="h-5 w-5 text-primary" />
                    <h2 className="font-display text-xl font-bold text-foreground">Double authentification (2FA)</h2>
                    {user.twofa_enabled && (
                        <span className="ml-auto inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold bg-primary text-primary-foreground">
                            <ShieldCheck className="h-3.5 w-3.5" /> Activée
                        </span>
                    )}
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                    Ajoute un code à 6 chiffres (via Google Authenticator, Authy, Microsoft Authenticator…)
                    en plus du mot de passe. Même si ton mot de passe fuite, l'accès reste protégé.
                </p>

                {/* Cas 1 : déjà activée → possibilité de désactiver */}
                {user.twofa_enabled ? (
                    !disableOpen ? (
                        <button
                            onClick={() => setDisableOpen(true)}
                            className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold border-2 border-destructive/40 text-destructive hover:bg-destructive/5"
                        >
                            <Lock className="h-4 w-4" /> Désactiver la 2FA
                        </button>
                    ) : (
                        <form onSubmit={disableTfa} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="block">
                                <span className="text-xs font-semibold text-muted-foreground">Mot de passe actuel</span>
                                <input type="password" value={disablePw} onChange={(e) => setDisablePw(e.target.value)}
                                    className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" autoComplete="current-password" />
                            </label>
                            <label className="block">
                                <span className="text-xs font-semibold text-muted-foreground">Code 2FA</span>
                                <input type="text" inputMode="numeric" maxLength={6} value={disableCode}
                                    onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ""))}
                                    className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm tracking-widest" placeholder="123456" />
                            </label>
                            <div className="md:col-span-2 flex justify-end gap-2">
                                <button type="button" onClick={() => setDisableOpen(false)}
                                    className="rounded-full px-5 py-2 text-sm font-semibold border border-border hover:bg-secondary">Annuler</button>
                                <button type="submit" disabled={tfaBusy}
                                    className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold bg-destructive text-white hover:opacity-90 disabled:opacity-50">
                                    {tfaBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Confirmer la désactivation
                                </button>
                            </div>
                        </form>
                    )
                ) : !tfaSetup ? (
                    /* Cas 2 : pas activée, pas encore lancée */
                    <button onClick={startTfaSetup} disabled={tfaBusy}
                        className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50">
                        {tfaBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Activer la 2FA
                    </button>
                ) : (
                    /* Cas 3 : configuration en cours → clé + confirmation */
                    <div className="space-y-4">
                        <ol className="text-sm text-foreground/90 space-y-2 list-decimal list-inside">
                            <li>Ouvre ton application d'authentification et choisis « Ajouter un compte » → « Saisir une clé ».</li>
                            <li>Nom du compte : <strong>Energy Market</strong>. Colle la clé ci-dessous :</li>
                        </ol>
                        <div className="flex items-center gap-2">
                            <code className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono tracking-widest break-all">
                                {tfaSetup.secret}
                            </code>
                            <button onClick={copySecret} title="Copier"
                                className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm hover:bg-secondary">
                                {tfaCopied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
                                {tfaCopied ? "Copié" : "Copier"}
                            </button>
                        </div>
                        <form onSubmit={activateTfa} className="flex flex-wrap items-end gap-3">
                            <label className="block">
                                <span className="text-xs font-semibold text-muted-foreground">Code affiché dans l'app</span>
                                <input type="text" inputMode="numeric" maxLength={6} autoFocus value={tfaCode}
                                    onChange={(e) => setTfaCode(e.target.value.replace(/\D/g, ""))}
                                    className="mt-1 w-40 rounded-lg border border-border bg-background px-3 py-2 text-lg text-center tracking-[0.3em]" placeholder="123456" />
                            </label>
                            <button type="submit" disabled={tfaBusy}
                                className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50">
                                {tfaBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Confirmer l'activation
                            </button>
                            <button type="button" onClick={() => { setTfaSetup(null); setTfaCode(""); }}
                                className="rounded-full px-4 py-2 text-sm font-semibold border border-border hover:bg-secondary">Annuler</button>
                        </form>
                    </div>
                )}
            </section>

            {/* Section : sessions / déconnexion */}
            <section className="rounded-3xl border border-border bg-card p-6 mt-6">
                <div className="flex items-center gap-2 mb-4">
                    <LogOut className="h-5 w-5 text-destructive" />
                    <h2 className="font-display text-xl font-bold text-foreground">Session</h2>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                    Tu peux te déconnecter ici. Pour invalider toutes les sessions ouvertes ailleurs,
                    change ton mot de passe ci-dessus (les anciens tokens restent valides jusqu'à expiration —
                    7 jours par défaut — mais sont rejetés au prochain login).
                </p>
                <button
                    onClick={() => { logout(); toast.success("Déconnecté"); window.location.href = "/"; }}
                    className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold border-2 border-destructive/40 text-destructive hover:bg-destructive/5"
                >
                    <LogOut className="h-4 w-4" />
                    Me déconnecter
                </button>
            </section>
        </div>
    );
}
